import { GatewayControleQuinquennal } from "@conformite/gateway";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { ControleQuinquennalApi } from "../controle-quinquennal.api";
import { controleQuinquennalKeys } from "./controle-quinquennal.keys";

export type CQMetriques =
  GatewayControleQuinquennal.ControleQuinquennalMetriquesAvecSiren;

export type LigneAMesurer = {
  id: string;
  idEntreprise: string;
  millesime: number;
};

/**
 * Un paquet = un aller-retour HubSpot groupé. Dix lignes remplissent la page en
 * quelques vagues sans multiplier les appels : HubSpot limite le débit par
 * salves de dix secondes, et sa saturation retombe en 500 côté gateway.
 */
const TAILLE_PAQUET = 10;

const decouper = <T>(lignes: T[], taille: number): T[][] =>
  Array.from({ length: Math.ceil(lignes.length / taille) }, (_, index) =>
    lignes.slice(index * taille, (index + 1) * taille)
  );

export type CQMetriquesResult = {
  /** Métriques reçues, indexées par id de contrôle. */
  parControle: Map<string, CQMetriques>;
  /** Vrai tant que le paquet portant cette ligne n'est pas revenu. */
  isPending: (id: string) => boolean;
};

/**
 * Métriques des contrôles affichés, chargées après la liste : leur calcul est
 * trop lourd pour la retarder (HubSpot, puis tous les questionnaires du
 * contrôle et de ses assujettis). Les paquets arrivent indépendamment, et
 * chaque ligne s'affiche dès que le sien est revenu.
 */
export const useCQMetriquesQuery = (
  lignes: LigneAMesurer[],
  enabled = true
): CQMetriquesResult => {
  const paquets = useMemo(
    () => decouper(lignes, TAILLE_PAQUET),
    // Les objets de ligne changent d'identité à chaque rendu de la liste : on
    // ne redécoupe que si la composition du lot change vraiment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lignes.map((ligne) => ligne.id).join("|")]
  );

  const queries = useQueries({
    queries: paquets.map((paquet) => ({
      queryKey: controleQuinquennalKeys.metriques(paquet.map(({ id }) => id)),
      queryFn: () => ControleQuinquennalApi.metriques(paquet),
      // Ce qui garantit la fraîcheur, c'est l'invalidation : enregistrer une
      // réponse invalide les listes, donc ces clés (cf. `metriques`). Ce délai
      // ne fait qu'éviter de relancer HubSpot à chaque remontage de la page
      // pendant une navigation aller-retour.
      staleTime: 60_000,
      enabled: enabled && paquet.length > 0,
    })),
  });

  return useMemo(() => {
    const parControle = new Map<string, CQMetriques>();
    const enAttente = new Set<string>();

    paquets.forEach((paquet, index) => {
      const { data } = queries[index] ?? {};
      paquet.forEach(({ id }) => {
        const metriques = data?.[id];
        if (metriques) parControle.set(id, metriques);
        else if (!data) enAttente.add(id);
      });
    });

    return { parControle, isPending: (id: string) => enAttente.has(id) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paquets, queries.map((query) => query.status).join("|")]);
};
